import { BehaviorSubject, catchError, tap, throwError } from 'rxjs';
import {
  BulkUploadResult,
  EligibilityStatusOverride,
  EligibilitySummary
} from '@app/core/models/appointment.model';
import {
  CreateAppointmentRequest,
  DownloadAppointmentsRequest,
  GetAllAppointmentsRequest,
  SyncApprovalRequest
} from '@app/core/models/requests';
import {
  CreateAppointmentResponse,
  DownloadAppointmentsResponse,
  GetAllAppointmentsResponse,
  GetAppointmentByIdResponse
} from '@app/core/models/responses';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpMethod, HttpService } from '../http/http.service';

import { APIConstants } from '@app/core/constants/api.constants';
import { EligibilityService } from '../eligibility/eligibility.service';
import { EmbedAuthService } from '../auth/embed-auth.service';
import { EmbedMessageToParentStatus } from '@app/core/models/embed.model';
import { Injectable } from '@angular/core';
import { InsuranceStatusOverride } from '@app/core/models/insurance.model';
import { SharedMethods } from '@app/shared/utils/shared-methods';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private $lastVisitedAppointmentId = new BehaviorSubject<number | undefined>(undefined);

  constructor(
    private _http: HttpService,
    private eligibilityService: EligibilityService,
    private embedAuthService: EmbedAuthService
  ) {}

  get lastVisitedAppointmentId() {
    return this.$lastVisitedAppointmentId.asObservable();
  }

  updateLastVisitedAppointmentId(appointmentId: number) {
    this.$lastVisitedAppointmentId.next(appointmentId);
  }

  getAllAppointments(requestParams: GetAllAppointmentsRequest) {
    return this._http.sendSecureRequest<GetAllAppointmentsResponse>({
      method: HttpMethod.POST,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedGetAllAppointment
        : APIConstants.getAllAppointment,
      body: requestParams
    });
  }

  // Get eligibility summary based to show up in the daily-huddle screen
  getEligibilitySummary(date: Date | Date[]) {
    return this._http.sendSecureRequest<EligibilitySummary>({
      method: HttpMethod.POST,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedGetAppointmentsSummary
        : APIConstants.getAppointmentsSummary,
      body: { date }
    });
  }

  // Upload file
  uploadFile(file: FormData) {
    return this._http.sendSecureRequest<BulkUploadResult>({
      method: HttpMethod.POST,
      url: APIConstants.importAppointments,
      headers: new HttpHeaders(),
      body: file
    });
  }

  createAppointment(data: CreateAppointmentRequest) {
    return this._http
      .sendSecureRequest<CreateAppointmentResponse>({
        method: HttpMethod.POST,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedCreateAppointment
          : APIConstants.createAppointment,
        headers: new HttpHeaders(),
        body: data
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this.eligibilityService.emitEligibilityChange(response.body.data.appointmentId);
          }
        })
      );
  }

  updateAppointment(appointmentId: number, data: CreateAppointmentRequest) {
    return this._http
      .sendSecureRequest<CreateAppointmentResponse>({
        method: HttpMethod.PUT,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedUpdateAppointment + appointmentId
          : APIConstants.updateAppointment + appointmentId,
        headers: new HttpHeaders(),
        body: data
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this.eligibilityService.emitEligibilityChange(response.body.data.appointmentId);
          }
        })
      );
  }

  getAppointmentById(appointmentId: number) {
    return this._http
      .sendSecureRequest<GetAppointmentByIdResponse>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? `${APIConstants.externalApiEmbedAppointments}/${appointmentId}`
          : `${APIConstants.getAppointmentById}${appointmentId}`,
        headers: new HttpHeaders()
      })
      .pipe(
        catchError((error) => {
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  cancelRetryEligibilityById(eligibilityId: number) {
    return this._http
      .sendSecureRequest<GetAppointmentByIdResponse>({
        method: HttpMethod.DELETE,
        url: this.embedAuthService.isEmbedded()
          ? `${APIConstants.embedCancelRetryById}/${eligibilityId}`
          : `${APIConstants.cancelRetryById}${eligibilityId}`,
        headers: new HttpHeaders()
      })
      .pipe(
        catchError((error) => {
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  updateTags(appointmentId: number, tags: { tag: string; description?: string }[]) {
    return this._http
      .sendSecureRequest<GetAppointmentByIdResponse>({
        method: HttpMethod.POST,
        url: this.embedAuthService.isEmbedded()
          ? `${APIConstants.externalApiEmbedAppointments}/${appointmentId}/tag`
          : `${APIConstants.getAppointmentById}${appointmentId}/tag`,
        headers: new HttpHeaders(),
        body: { tags, appointmentId }
      })
      .pipe(
        catchError((error) => {
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  updateRequestSyncStatus(body: SyncApprovalRequest, isEmbedded = false) {
    return this._http
      .sendSecureRequest<any>({
        method: HttpMethod.POST,
        url: isEmbedded
          ? APIConstants.externalApiEmbedUpdateSyncStatus
          : APIConstants.updateSyncStatus,
        headers: new HttpHeaders(),
        body
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            if (body.appointmentId)
              this.eligibilityService.emitEligibilityChange(body.appointmentId);
          }
        })
      );
  }

  downloadAppointments(body: DownloadAppointmentsRequest) {
    return this._http.sendSecureRequest<DownloadAppointmentsResponse>({
      method: HttpMethod.POST,
      url: `${
        !this.embedAuthService.isEmbedded()
          ? APIConstants.downloadAppointments
          : APIConstants.externalApiEmbedDownloadAppointments
      }`,
      headers: new HttpHeaders(),
      body
    });
  }

  downloadAppointmentsStatus(fileId: number) {
    return this._http.sendSecureRequest<DownloadAppointmentsResponse>({
      method: HttpMethod.GET,
      url: `${
        !this.embedAuthService.isEmbedded()
          ? APIConstants.downloadAppointments
          : APIConstants.externalApiEmbedDownloadAppointments
      }/${fileId}`,
      headers: new HttpHeaders()
    });
  }

  updateEligibilityStatusOverride(body: {
    eligibilityId: number;
    statusOverride: EligibilityStatusOverride;
  }) {
    return this._http.sendSecureRequest({
      method: HttpMethod.PUT,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedUpdateEligibilityStatusOverride
        : APIConstants.updateEligibilityStatusOverride,
      headers: new HttpHeaders(),
      body
    });
  }

  deleteEligibilityStatusOverride(queryParams: { eligibilityId: number }) {
    return this._http.sendSecureRequest({
      method: HttpMethod.DELETE,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedDeleteEligibilityStatusOverride
        : APIConstants.deleteEligibilityStatusOverride,
      headers: new HttpHeaders(),
      params: new HttpParams({
        fromObject: queryParams
      })
    });
  }

  updateInsuranceOverrideStatus(body: {
    eligibilityId: number;
    statusOverride: InsuranceStatusOverride;
  }) {
    return this._http.sendSecureRequest({
      method: HttpMethod.PUT,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedUpdateInsuranceStatusOverride
        : APIConstants.updateInsuranceStatusOverride,
      headers: new HttpHeaders(),
      body
    });
  }

  deleteInsuranceOverrideStatus(queryParams: { eligibilityId: number }) {
    return this._http.sendSecureRequest({
      method: HttpMethod.DELETE,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedDeleteInsuranceStatusOverride
        : APIConstants.deleteInsuranceStatusOverride,
      headers: new HttpHeaders(),
      params: new HttpParams({
        fromObject: queryParams
      })
    });
  }
}
